<template>
  <Modal v-if="grimoire.modal === 'roles' && nontravelers >= 5" class="roles" @close="grimoire.toggleModal(null)">
    <h3>
      {{
        t("modal.roles.titleStart") + nontravelers + t("modal.roles.titleEnd")
      }}
    </h3>
    <RoleTeamList v-for="(teamRoles, team) in roleSelection" :key="team" :team="team" :team-roles="teamRoles"
      :composition-count="composition?.[team as keyof GameComposition]" :multiple-allowed="multipleAllowedByTeam[team]" />
    <ul class="tokens">
      <li v-for="role in fabledWithSetup" :key="role?.id" :class="['fabled', 'selected']">
        <Token :role="role" />
        <font-awesome-icon icon="exclamation-triangle" class="fa fa-exclamation-triangle" />
      </li>
    </ul>
    <div v-if="hasSelectedSetupRoles || fabledWithSetup.length" class="warning">
      <font-awesome-icon icon="exclamation-triangle" class="fa fa-exclamation-triangle" />
      <span>{{ t("modal.roles.warning") }}</span>
    </div>
    <div class="button-group">
      <button class="button" :class="{
        disabled: selectedRoles > nontravelers || !selectedRoles,
      }" @click="assignRoles">
        <font-awesome-icon icon="people-arrows" class="fa fa-people-arrows" />
        {{
          t("modal.roles.assignStart") +
          selectedRoles +
          t("modal.roles.assignEnd")
        }}
      </button>
      <div class="button" @click="selectRandomRoles">
        <font-awesome-icon icon="random" class="fa fa-random" />
        {{ t("modal.roles.shuffle") }}
      </div>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { Modal, RoleTeamList, Token } from "@/components";
import { useGrimoireStore, useLocaleStore, usePlayersStore } from "@/stores";
import type {
  GameComposition,
  Player,
  Role,
  RoleGroup,
  SelectableRole,
} from "@/types";

const locale = useLocaleStore();
const t = locale.t;

const randomElement = <T,>(arr: T[]): T => {
  if (arr.length === 0) throw new Error("Cannot select from empty array");
  return arr[Math.floor(Math.random() * arr.length)]!;
};

const grimoire = useGrimoireStore();
const playersStore = usePlayersStore();

const roleSelection = ref<RoleGroup>({});

const fabled = computed(() => playersStore.fabled);
const nontravelers = computed(() => playersStore.nontravelers);
const players = computed(() => playersStore.players);
const roles = computed(() => grimoire.roles);
const edition = computed(() => grimoire.edition);

const composition = computed(() =>
  grimoire.getGameComposition(nontravelers.value),
);

const selectedRoles = computed(() => {
  return Object.values(roleSelection.value)
    .map((roles: SelectableRole[]) =>
      roles.reduce((a: number, { selected }) => a + selected, 0),
    )
    .reduce((a: number, b: number) => a + b, 0);
});

const hasSelectedSetupRoles = computed(() => {
  return Object.values(roleSelection.value).some((roles: SelectableRole[]) =>
    roles.some((role: SelectableRole) => role?.selected && role?.setup),
  );
});

const alignmentByTeam: Record<string, string> = {
  townsfolk: "good",
  outsider: "good",
  minion: "evil",
  demon: "evil",
};

const multipleAllowedByTeam = computed(() => {
  const result: Record<string, boolean> = {};
  for (const [team, alignment] of Object.entries(alignmentByTeam)) {
    const feature = alignment + "-duplicate";
    result[team] =
      [...roles.value.values()].some((role) => playersStore.hasFeature(role, feature)) ||
      fabled.value.some((role) => playersStore.hasFeature(role, feature));
  }
  return result;
});

const fabledWithSetup = computed(() => {
  let res: Role[] = [];
  for (const fable of fabled.value) {
    if (fable.setup) {
      res.push(fable);
    }
  }
  return res;
});

const selectRandomRoles = () => {
  roleSelection.value = {};
  roles.value.forEach((role: Role) => {
    const selectableRole: SelectableRole = { ...role, selected: 0 };
    if (!roleSelection.value[role?.team || "unknown"]) {
      roleSelection.value[role?.team || "unknown"] = [];
    }
    roleSelection.value[role?.team || "unknown"]?.push(selectableRole);
  });
  delete roleSelection.value["traveler"];
  if (composition.value) {
    Object.keys(composition.value).forEach((team: string) => {
      const teamKey = team as keyof GameComposition;
      for (let x = 0; x < composition.value[teamKey]; x++) {
        if (roleSelection.value[team]) {
          const available = roleSelection.value[team].filter(
            (role: SelectableRole) => !role?.selected,
          );
          if (available.length) {
            randomElement(available).selected = 1;
          }
        }
      }
    });
  }
};

const assignRoles = () => {
  if (selectedRoles.value <= nontravelers.value && selectedRoles.value) {
    // generate list of selected roles and randomize it
    const roles = Object.values(roleSelection.value)
      .flatMap((roles: SelectableRole[]) =>
        roles
          // duplicate roles selected more than once and filter unselected
          .reduce(
            (a: SelectableRole[], r: SelectableRole) => [
              ...a,
              ...new Array(r.selected).fill(r),
            ],
            [],
          ),
      )
      .map(
        (a: SelectableRole) => [Math.random(), a] as [number, SelectableRole],
      )
      .sort(
        (a: [number, SelectableRole], b: [number, SelectableRole]) =>
          a[0] - b[0],
      )
      .map((a: [number, SelectableRole]) => a[1]);
    players.value.forEach((player: Player) => {
      if (player.role?.team !== "traveler" && roles.length) {
        const value = roles.pop();
        playersStore.update({
          player,
          property: "role",
          value,
        });
      }
      playersStore.update({
        player,
        property: "alignment",
        value: null,
      });
    });
    grimoire.toggleModal(null);
  }
};
onMounted(() => {
  if (!Object.keys(roleSelection.value).length) {
    selectRandomRoles();
  }
});

watch(edition, () => {
  selectRandomRoles();
});
</script>

<style lang="scss" scoped>
@use "../../vars.scss" as *;

ul.tokens {
  padding-left: 8vmin;
  margin-top: 5px;

  li {
    border-radius: 50%;
    width: 5vmax;
    margin: 5px;
    transition: transform 250ms;
    will-change: transform;

    &.townsfolk {
      box-shadow:
        0 0 10px var(--townsfolk),
        0 0 10px #004cff;
    }

    &.outsider {
      box-shadow: 0 0 10px var(--outsider);
    }

    &.minion {
      box-shadow: 0 0 10px var(--minion);
    }

    &.demon {
      box-shadow: 0 0 10px var(--demon);
    }

    &.traveler {
      box-shadow: 0 0 10px var(--traveler);
    }

    &.fabled {
      box-shadow: 0 0 10px var(--fabled);
    }

    &.loric {
      box-shadow: 0 0 10px var(--loric);
    }

    &:hover {
      transform: scale(1.2);
      z-index: 10;
    }

    &.selected .fa-exclamation-triangle {
      display: block;
    }

    .fa-exclamation-triangle {
      position: absolute;
      color: red;
      text-shadow: 0 0 2px black;
      filter: drop-shadow(0 0 3px black);
      top: 5px;
      right: -5px;
      font-size: 150%;
      display: none;
    }
  }
}

.roles .modal {
  .multiple {
    display: block;
    text-align: center;
    cursor: pointer;
    margin-top: 20px;

    &.checked,
    &:hover {
      color: red;
      text-shadow: 0 0 2px black;
    }

    svg {
      margin-right: 5px;
    }

    input {
      display: none;
    }
  }

  .warning {
    color: red;
    text-shadow: 0 0 2px black;
    position: absolute;
    bottom: 20px;
    right: 20px;
    z-index: 10;

    svg {
      font-size: 150%;
      vertical-align: middle;
    }

    span {
      display: none;
      text-align: center;
      position: absolute;
      right: -20px;
      bottom: 30px;
      width: 420px;
      background: var(--background-color);
      backdrop-filter: blur(3px);
      padding: 5px;
      border-radius: 10px;
      border: 2px solid black;
    }

    &:hover span {
      display: block;
    }
  }
}
</style>
